import React from 'react';
import { useSelector } from 'react-redux';

import { Row, Col } from '@zillow/zrs-comet';
import { Spacer } from '@zillow/constellation';

import { DetailedDescription } from 'app/shared/components/postingPath/DetailedDescription';
import AmenitiesAndRules from 'app/shared/components/postingPath/AmenitiesAndRules';
import ContactInformation from 'app/shared/components/postingPath/ContactInformation';
import EnableApplicationsRadio from 'app/shared/components/postingPath/EnableApplicationsRadio';
import HousingConnector from 'app/shared/components/postingPath/HousingConnector';
import LeadGuaranteeBanner from 'app/shared/components/postingPath/LeadGuaranteeBanner';
import ListingDetails from 'app/shared/components/postingPath/ListingDetails';
import ListingStatus from 'app/shared/components/postingPath/ListingStatus';
import Media from 'app/shared/components/postingPath/Media';
import PaidInclusionOverview from 'app/shared/components/paidInclusion/ListingOverview';
import Scheduler from 'app/shared/components/postingPath/Scheduler';
import { UserSelectors, PropertiesSelectors } from 'app/shared/selectors';
import RenewListing from './components/renewListing';

const SingleFamilyForm = ({
    address,
    canUpdateApplications,
    cityStateZip,
    contactNameRef,
    contactPhoneRef,
    descriptionRef,
    errorObj,
    floorplansRef,
    formData,
    forRentByRef,
    immediateUpdateAndSave,
    isApplicationsAcceptedRef,
    isInputFormDisabled,
    isListingPhotoProcessing,
    isPaymentValid,
    leaseDurationRef,
    listing,
    propertyId,
    rentPriceRef,
    saveFloorplanData,
    savePropertyData,
    setFailedRenewalData,
    setHasErrors,
    setSuccessfulRenewalData,
    statusMessage,
    updateErrorObj,
    updateFormData,
    updateListingPhotoProcessingStatus,
    updateOnFocus,
    updateStatusMessage,
    validateRequiredFields,
}) => {
    const { features: { canUpdateListing } } = useSelector(state => UserSelectors.makeUser(state));
    const isSeattleHousingConnector = useSelector(state => UserSelectors.makeIsSeattleHousingConnector(state));
    const { leadGuarantee } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const isPaymentRequired = Boolean(listing.paidInclusion && listing.paidInclusion.isPaymentRequired);
    const {
        isEligible: isEligibleForLeadGuarantee,
    } = leadGuarantee;
    const {
        seattleHousingConnectorStatus,
        syndication,
        isApplicationsAccepted,
    } = formData;

    // renew (extend) listing
    const { paidInclusion } = listing;
    const { isEnabled: isListingInNucleusMarket } = paidInclusion || {};

    const logicForNucleusMarket =
        formData.active === true &&
        isListingInNucleusMarket === true &&
        isPaymentValid === false &&
        isPaymentRequired === false;

    const logicForNonNucleusMarket =
        formData.active === true &&
        isListingInNucleusMarket === false &&
        isPaymentValid === false &&
        isPaymentRequired === false;

    const shouldDisplayRenewListing = (logicForNucleusMarket || logicForNonNucleusMarket)
        && !formData.restrictions.isUpdateListingRestricted;

    // lead guarantee
    const renderLeadGuaranteeBanner = () => {
        const heading = 'Backed by our Lead Guarantee';
        const body = 'If you don\'t receive a lead within a week, it\'s free to list until you do (for up to 8 weeks).';
        const actionLabel = 'Explore Lead Guarantee';
        const actionPath = 'https://help.zillowrentalmanager.com/hc/en-us/articles/360048462014';

        return (
            <LeadGuaranteeBanner
                heading={heading}
                body={body}
                actionLabel={actionLabel}
                actionPath={actionPath}
                actionTarget="_blank"
            />
        );
    };

    return (
        <Row>
            <Col xs={0} md={2} />
            <Col xs={12} md={8}>
                {shouldDisplayRenewListing && (
                    <RenewListing
                        listing={listing}
                        propertyId={propertyId}
                        validateRequiredFields={validateRequiredFields}
                        updateStatusMessage={updateStatusMessage}
                        setHasErrors={setHasErrors}
                        setSuccessfulRenewalData={setSuccessfulRenewalData}
                        setFailedRenewalData={setFailedRenewalData}
                    />
                )}
                {!formData.isFeed && formData.active && (
                    <PaidInclusionOverview />
                )}
                {formData.active && (
                    <ListingStatus
                        canUpdateListing={canUpdateListing}
                        syndication={syndication}
                        isCommunity={false}
                        listing={listing}
                        address={address}
                        cityStateZip={cityStateZip}
                    />
                )}

                {(formData.active === false && isEligibleForLeadGuarantee) && (
                    <Spacer marginBottom="md">
                        {renderLeadGuaranteeBanner()}
                    </Spacer>
                )}

                <ListingDetails
                    descriptionRef={descriptionRef}
                    errorObj={errorObj}
                    floorplansRef={floorplansRef}
                    formData={formData}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    isInputFormDisabled={isInputFormDisabled}
                    leaseDurationRef={leaseDurationRef}
                    rentPriceRef={rentPriceRef}
                    saveFloorplanData={saveFloorplanData}
                    savePropertyData={savePropertyData}
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                />
                <AmenitiesAndRules
                    errorObj={errorObj}
                    formData={formData}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    propertyId={propertyId}
                    updateFormData={updateFormData}
                    updateStatusMessage={updateStatusMessage}
                    isInputFormDisabled={isInputFormDisabled}
                />
                <DetailedDescription
                    descriptionRef={descriptionRef}
                    errorObj={errorObj}
                    formData={formData}
                    isInputFormDisabled={isInputFormDisabled}
                    savePropertyData={savePropertyData}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                />
                <Media
                    errorObj={errorObj}
                    formData={formData}
                    hasError={Boolean(errorObj.photos)}
                    isInputFormDisabled={isInputFormDisabled}
                    isListingPhotoProcessing={isListingPhotoProcessing}
                    propertyId={propertyId}
                    statusMessage={statusMessage}
                    tour={formData.tour}
                    tourType="singleFamily"
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateListingPhotoProcessingStatus={updateListingPhotoProcessingStatus}
                    updateStatusMessage={updateStatusMessage}
                />
                <Spacer marginY="md" />
                <ContactInformation
                    contactNameRef={contactNameRef}
                    contactPhoneRef={contactPhoneRef}
                    errorObj={errorObj}
                    formData={formData}
                    forRentByRef={forRentByRef}
                    immediateUpdateAndSave={immediateUpdateAndSave}
                    isInputFormDisabled={isInputFormDisabled}
                    isPaymentRequired={isPaymentRequired}
                    savePropertyData={savePropertyData}
                    updateErrorObj={updateErrorObj}
                    updateFormData={updateFormData}
                    updateOnFocus={updateOnFocus}
                    updateStatusMessage={updateStatusMessage}
                />
                <Scheduler
                    isInputFormDisabled={isInputFormDisabled}
                    schedule={formData.schedule}
                    updateStatusMessage={updateStatusMessage}
                    updateFormData={updateFormData}
                    propertyId={propertyId}
                />
                {isSeattleHousingConnector && (
                    <React.Fragment>
                        <HousingConnector
                            initSelectedOption={seattleHousingConnectorStatus}
                            handleSelectChange={immediateUpdateAndSave}
                        />
                    </React.Fragment>
                )}
                {canUpdateApplications && (
                    <React.Fragment>
                        <EnableApplicationsRadio
                            isApplicationsAcceptedRef={isApplicationsAcceptedRef}
                            errorObj={errorObj}
                            updateErrorObj={updateErrorObj}
                            isApplicationsAccepted={isApplicationsAccepted}
                            handleSelectionChange={immediateUpdateAndSave}
                        />
                    </React.Fragment>
                )}
            </Col>
            <Col xs={0} md={2} />
        </Row>
    );
};

export default SingleFamilyForm;
